'use client'

import { useRouter } from 'next/navigation'
import * as React from 'react'

import { setLocaleAction } from '@/lib/crud/actions'
import { useT } from '@/lib/i18n/client'
import { locales, type Locale } from '@/lib/i18n/config'
import { cn } from '@/lib/utils'

/** VI | EN toggle. Light variant is for the navy sidebar; `onLight` for the login page. */
export function LocaleSwitch({ onLight }: { onLight?: boolean }) {
  const { locale, t } = useT()
  const router = useRouter()
  const [pending, startTransition] = React.useTransition()

  const choose = (next: Locale) => {
    if (next === locale) return
    startTransition(async () => {
      await setLocaleAction(next)
      router.refresh()
    })
  }

  return (
    <div role="group" aria-label={t('ui.language')} className="flex gap-1 px-3" aria-busy={pending}>
      {locales.map((code) => (
        <button
          key={code}
          type="button"
          lang={code}
          aria-pressed={code === locale}
          onClick={() => choose(code)}
          className={cn(
            'rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wide transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
            onLight
              ? code === locale
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
              : code === locale
                ? 'bg-white/15 text-white'
                : 'text-sidebar-foreground/70 hover:bg-white/5 hover:text-white',
          )}
        >
          {code}
        </button>
      ))}
    </div>
  )
}
